/**
 * Vehicle Services Hook
 * React hook jármű szervizek kezeléséhez
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { VehicleService } from '../types/vehicle';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  getVehicleServices,
  getOrganizationServices,
  createVehicleService,
  updateVehicleService,
  deleteVehicleService
} from '../services/vehicleService';

export function useVehicleServices(vehicleId?: string, limitCount?: number) {
  const { user } = useAuth();
  const [services, setServices] = useState<VehicleService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener for services
  useEffect(() => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const servicesRef = collection(db, 'vehicleServices');
    const constraints: any[] = [orderBy('date', 'desc')];

    if (vehicleId) {
      constraints.unshift(where('vehicleId', '==', vehicleId));
    } else {
      constraints.unshift(where('organizationId', '==', user.organizationId));
    }

    const q = query(servicesRef, ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const servicesData = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          date: doc.data().date?.toDate() || new Date(),
          nextServiceDate: doc.data().nextServiceDate?.toDate(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          documents: doc.data().documents?.map((d: any) => ({
            ...d,
            uploadedAt: d.uploadedAt?.toDate() || new Date()
          })) || []
        })) as VehicleService[];

        // Limit alkalmazása ha van
        const limitedServices = limitCount ? servicesData.slice(0, limitCount) : servicesData;

        setServices(limitedServices);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message || 'Hiba a szervizek betöltése során');
        console.error('Error loading vehicle services:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.organizationId, vehicleId, limitCount]);

  // Szervizek betöltése (manual refresh)
  const loadServices = useCallback(async () => {
    if (!user?.organizationId) {
      return;
    }

    try {
      setLoading(true);
      const data = vehicleId
        ? await getVehicleServices(vehicleId, limitCount)
        : await getOrganizationServices(user.organizationId, limitCount);
      setServices(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Hiba a szervizek betöltése során');
      console.error('Error loading vehicle services:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.organizationId, vehicleId, limitCount]);

  // Új szerviz létrehozása
  const create = useCallback(
    async (serviceData: Omit<VehicleService, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
      if (!user?.organizationId || !user?.uid) {
        throw new Error('Nincs bejelentkezve');
      }

      try {
        const serviceId = await createVehicleService(serviceData, user.organizationId, user.uid);
        return serviceId;
      } catch (err: any) {
        setError(err.message || 'Hiba a szerviz létrehozása során');
        throw err;
      }
    },
    [user?.organizationId, user?.uid]
  );

  // Szerviz frissítése
  const update = useCallback(
    async (serviceId: string, updates: Partial<VehicleService>): Promise<void> => {
      try {
        await updateVehicleService(serviceId, updates);
      } catch (err: any) {
        setError(err.message || 'Hiba a szerviz frissítése során');
        throw err;
      }
    },
    []
  );

  // Szerviz törlése
  const remove = useCallback(async (serviceId: string): Promise<void> => {
    try {
      await deleteVehicleService(serviceId);
    } catch (err: any) {
      setError(err.message || 'Hiba a szerviz törlése során');
      throw err;
    }
  }, []);

  return {
    services,
    loading,
    error,
    loadServices,
    create,
    update,
    remove
  };
}
